<script setup lang="ts">
import type { AppInputDefinition, AppInputProblem } from '~/utils/app-inputs'
import { answerString } from '~/utils/app-inputs'

/**
 * One setup question, drawn from its definition: the label and why it is
 * asked, the control its type calls for, where to get the value, what happens
 * when it is left for later, and — once there is one — what is wrong with the
 * answer, beside the control it is about.
 *
 * Presentational: the form owns the answers and decides when a problem is
 * worth showing (after the field was left, or once the customer tried to go
 * on), so a field never turns red while it is being typed into for the first
 * time.
 */
const props = withDefaults(
  defineProps<{
    appCode: string
    def: AppInputDefinition
    modelValue: string | number | boolean | undefined
    problem?: AppInputProblem | null
    showProblem?: boolean
    /**
     * A secret the instance already holds, on a reinstall of the same app:
     * `null` when it is too short to show any of, else its last characters.
     * `undefined` when there is nothing kept.
     */
    keptHint?: string | null
    /** The account's address, for a field that defaults to it. */
    accountEmail?: string | null
    disabled?: boolean
  }>(),
  {
    problem: null,
    showProblem: false,
    keptHint: undefined,
    accountEmail: null,
    disabled: false,
  },
)

const emit = defineEmits<{
  'update:modelValue': [value: string | number | boolean]
  blur: []
}>()

const { t } = useI18n()
const copy = useAppInputCopy()
const id = useId()

const text = computed(() => copy.field(props.appCode, props.def))
const reveal = ref(false)
const guideOpen = ref(false)
/** On a reinstall a kept secret shows as kept until the customer asks to replace it. */
const replacing = ref(false)
const keeping = computed(() => props.keptHint !== undefined && !replacing.value)

const empty = computed(() => answerString(props.modelValue as never) === '')
const problemText = computed(() =>
  props.problem && props.showProblem ? copy.problem(props.appCode, props.def, props.problem) : null,
)

const vendorHost = computed(() => {
  try {
    return props.def.obtain_url ? new URL(props.def.obtain_url).hostname.replace(/^www\./, '') : null
  } catch {
    return null
  }
})

const accountEmailDefault = computed(() => props.def.default === '$account_email')
const placeholder = computed(() => {
  if (accountEmailDefault.value && props.accountEmail) {
    return t('appInputs.accountEmailPlaceholder', { email: props.accountEmail })
  }
  return text.value.placeholder ?? undefined
})

const selectItems = computed(() =>
  (props.def.options ?? []).map((option) => {
    const words = copy.option(props.appCode, props.def, option.value)
    return {
      value: option.value,
      label: words.label,
      description: words.description ?? undefined,
      recommended: option.recommended === true,
    }
  }),
)

const describedBy = computed(() =>
  [`${id}-help`, problemText.value ? `${id}-problem` : null].filter(Boolean).join(' '),
)

function update(value: string | number | boolean | null | undefined) {
  emit('update:modelValue', value ?? '')
}

function replace() {
  replacing.value = true
  update('')
}

function keep() {
  replacing.value = false
  update('')
}
</script>

<template>
  <div class="space-y-2" :data-testid="`app-input-${def.key}`" :data-need="def.need">
    <!-- The terms the customer accepts: one checkbox, the link beside it. -->
    <template v-if="def.type === 'toggle' && def.must_accept">
      <div
        class="rounded-lg border p-3 sm:p-4"
        :class="problemText ? 'border-error-300 dark:border-error-700 bg-error-50/40 dark:bg-error-950/20' : 'border-default bg-elevated/40'"
      >
        <UCheckbox
          :id="id"
          :model-value="modelValue === true"
          :disabled="disabled"
          :aria-describedby="describedBy"
          :aria-invalid="!!problemText"
          @update:model-value="(value: boolean | 'indeterminate') => update(value === true)"
        >
          <template #label>
            <span class="text-sm font-medium text-highlighted">{{ text.label }}</span>
          </template>
          <template #description>
            <span :id="`${id}-help`" class="block text-sm text-muted">{{ text.help }}</span>
            <ULink
              v-if="def.obtain_url"
              :to="def.obtain_url"
              target="_blank"
              rel="noopener noreferrer"
              class="mt-1 inline-flex items-center gap-1 text-sm font-medium text-primary"
            >
              {{ $t('appInputs.readTerms') }}
              <UIcon name="i-heroicons-arrow-top-right-on-square" class="size-3.5" aria-hidden="true" />
            </ULink>
          </template>
        </UCheckbox>
      </div>
    </template>

    <template v-else>
      <div class="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <label :for="id" class="text-sm font-medium text-highlighted">{{ text.label }}</label>
        <UBadge
          v-if="def.need === 'required'"
          color="neutral"
          variant="subtle"
          size="sm"
          :label="$t('appInputs.need.required')"
        />
        <UBadge
          v-else-if="def.need === 'recommended'"
          color="primary"
          variant="subtle"
          size="sm"
          :label="$t('appInputs.need.recommended')"
        />
        <span v-else class="text-xs text-muted">{{ $t('appInputs.need.optional') }}</span>
      </div>
      <p :id="`${id}-help`" class="text-sm text-muted">{{ text.help }}</p>

      <!-- A secret the instance already holds: kept unless replaced. -->
      <div
        v-if="def.type === 'secret' && keeping"
        class="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-default bg-elevated/40 px-3 py-2.5"
        data-testid="app-input-kept"
      >
        <span class="flex items-center gap-2 text-sm text-default">
          <UIcon name="i-heroicons-lock-closed" class="size-4 text-muted" aria-hidden="true" />
          {{ keptHint ? $t('appInputs.keptWithHint', { hint: keptHint }) : $t('appInputs.kept') }}
        </span>
        <UButton size="xs" variant="outline" color="neutral" :disabled="disabled" @click="replace">
          {{ $t('appInputs.replace') }}
        </UButton>
      </div>

      <URadioGroup
        v-else-if="def.type === 'select'"
        :id="id"
        :model-value="String(modelValue ?? '')"
        :items="selectItems"
        variant="card"
        orientation="vertical"
        :disabled="disabled"
        :aria-describedby="describedBy"
        :ui="{ fieldset: 'grid grid-cols-1 sm:grid-cols-2 gap-2', item: 'items-start' }"
        @update:model-value="(value: unknown) => update(String(value))"
      >
        <template #label="{ item }">
          <span class="flex flex-wrap items-center gap-2">
            <span class="font-medium">{{ (item as any).label }}</span>
            <UBadge
              v-if="(item as any).recommended"
              color="primary"
              variant="soft"
              size="sm"
              :label="$t('appInputs.recommendedOption')"
            />
          </span>
        </template>
      </URadioGroup>

      <USwitch
        v-else-if="def.type === 'toggle'"
        :id="id"
        :model-value="modelValue === true"
        :disabled="disabled"
        :aria-describedby="describedBy"
        :label="modelValue === true ? $t('appInputs.toggleOn') : $t('appInputs.toggleOff')"
        @update:model-value="(value: boolean) => update(value)"
      />

      <UInputNumber
        v-else-if="def.type === 'number'"
        :id="id"
        :model-value="typeof modelValue === 'number' ? modelValue : modelValue === '' || modelValue === undefined ? undefined : Number(modelValue)"
        :min="def.min"
        :max="def.max"
        :disabled="disabled"
        :aria-describedby="describedBy"
        :aria-invalid="!!problemText"
        class="w-full sm:w-48"
        @update:model-value="(value: number | null) => update(value ?? '')"
        @blur="emit('blur')"
      />

      <div v-else class="space-y-1.5">
        <UInput
          :id="id"
          :model-value="String(modelValue ?? '')"
          :type="def.type === 'secret' && !reveal ? 'password' : def.type === 'email' ? 'email' : 'text'"
          :placeholder="placeholder"
          :disabled="disabled"
          :maxlength="def.max_length ?? 1024"
          :autocomplete="def.type === 'secret' ? 'off' : def.type === 'email' ? 'email' : 'off'"
          :spellcheck="false"
          :aria-describedby="describedBy"
          :aria-invalid="!!problemText"
          :color="problemText ? 'error' : undefined"
          :highlight="!!problemText"
          :ui="{ base: def.type === 'secret' ? 'font-mono' : '' }"
          class="w-full"
          @update:model-value="(value: string | number) => update(String(value))"
          @blur="emit('blur')"
        >
          <template v-if="def.type === 'secret'" #trailing>
            <UButton
              variant="ghost"
              color="neutral"
              size="xs"
              :icon="reveal ? 'i-heroicons-eye-slash' : 'i-heroicons-eye'"
              :aria-label="reveal ? $t('appInputs.hide') : $t('appInputs.show')"
              @click="reveal = !reveal"
            />
          </template>
        </UInput>
        <UButton
          v-if="def.type === 'secret' && keptHint !== undefined && replacing"
          variant="link"
          size="xs"
          color="neutral"
          class="px-0"
          @click="keep"
        >
          {{ $t('appInputs.keepCurrent') }}
        </UButton>
      </div>
    </template>

    <p
      v-if="problemText"
      :id="`${id}-problem`"
      role="alert"
      class="flex items-start gap-1.5 text-sm text-error-700 dark:text-error-400"
    >
      <UIcon name="i-heroicons-exclamation-circle" class="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <span>{{ problemText }}</span>
    </p>

    <!-- Left for later: nobody is stuck here without a vendor account yet. -->
    <p
      v-else-if="def.need === 'recommended' && empty && !keeping && text.later"
      class="flex items-start gap-1.5 text-sm text-muted"
      data-testid="app-input-later"
    >
      <UIcon name="i-heroicons-light-bulb" class="mt-0.5 size-4 shrink-0 text-primary" aria-hidden="true" />
      <span>{{ $t('appInputs.laterTip', { later: text.later }) }}</span>
    </p>

    <!-- Where do I get this? -->
    <div v-if="text.steps.length || def.obtain_url && !def.must_accept" class="pt-0.5">
      <UButton
        variant="link"
        size="xs"
        color="primary"
        class="px-0"
        :trailing-icon="guideOpen ? 'i-heroicons-chevron-up' : 'i-heroicons-chevron-down'"
        :aria-expanded="guideOpen"
        :aria-controls="`${id}-guide`"
        @click="guideOpen = !guideOpen"
      >
        {{ $t('appInputs.whereToGet') }}
      </UButton>
      <div
        v-show="guideOpen"
        :id="`${id}-guide`"
        class="mt-2 space-y-3 rounded-lg border border-default bg-elevated/40 p-3 sm:p-4"
        data-testid="app-input-guide"
      >
        <ol v-if="text.steps.length" class="space-y-2">
          <li v-for="(step, index) in text.steps" :key="index" class="flex items-start gap-3 text-sm">
            <span
              class="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary"
              aria-hidden="true"
            >{{ index + 1 }}</span>
            <span class="pt-0.5 text-default">{{ step }}</span>
          </li>
        </ol>
        <UButton
          v-if="def.obtain_url"
          :to="def.obtain_url"
          target="_blank"
          rel="noopener noreferrer"
          size="sm"
          variant="soft"
          trailing-icon="i-heroicons-arrow-top-right-on-square"
        >
          {{ vendorHost ? $t('appInputs.openSite', { site: vendorHost }) : $t('appInputs.openPage') }}
        </UButton>
      </div>
    </div>
  </div>
</template>
